import { useMutation, useQueryClient } from "@tanstack/react-query";
import { qk } from "../queries/keys";
import { api } from "../runtime";
import type { ApiKey, LeadImport, LeadImportPreview, LeadMapping, OpportunitySource } from "../types";

export function useUploadLeadFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; storeId: string; defaultSource: OpportunitySource }) => {
      const form = new FormData();
      form.append("file", input.file);
      form.append("storeId", input.storeId);
      form.append("defaultSource", input.defaultSource);
      return api.post<{ import: LeadImport; preview: LeadImportPreview }>("/v1/lead-imports", form);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.leadImports.all }),
  });
}

/** How the first rows come out with a mapping (no side effects). */
export function usePreviewLeadMapping(importId: string) {
  return useMutation({
    mutationFn: (mapping: LeadMapping) => api.post<LeadImportPreview>(`/v1/lead-imports/${importId}/preview`, { mapping }),
  });
}

export function useStartLeadImport(importId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { mapping: LeadMapping; defaultSource?: OpportunitySource }) =>
      (await api.post<{ import: LeadImport }>(`/v1/lead-imports/${importId}/start`, input)).import,
    onSuccess: (imp) => {
      qc.setQueryData(qk.leadImports.detail(importId), imp);
      void qc.invalidateQueries({ queryKey: qk.leadImports.all });
    },
  });
}

/** Discards a draft, or stops a running import. */
export function useCancelLeadImport(importId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ deleted: boolean; import: LeadImport | null }>(`/v1/lead-imports/${importId}/cancel`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.leadImports.all }),
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { storeId: string; name: string }) => api.post<{ apiKey: ApiKey; key: string }>("/v1/api-keys", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete<{ apiKey: ApiKey }>(`/v1/api-keys/${id}`)).apiKey,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["api-keys"] }),
  });
}

/** Saves a downloaded file in the browser. */
export function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadLeadTemplate(format: "csv" | "xlsx") {
  return api.download("/v1/lead-imports/template", { query: { format } }).then((blob) => saveBlob(blob, `rogue-leads-template.${format}`));
}
